/**
 * KCIS™ Pro — Escalation suppress/elevate controls (Sprint 6B4).
 *
 * Side-by-side controls for a single escalation flag. NEVER mutates engine
 * escalation — every action appends a new row to
 * `nutrient_escalation_actions` and writes an immutable audit log entry.
 *
 * Compliance: reason is REQUIRED on both suppress and elevate. The UI
 * blocks submission with an empty reason.
 */
import { useState } from "react";
import { ShieldAlert, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { Button } from "@/components/ui/button";

export default function EscalationControls({ assessmentId, escalation, canSuppress, canElevate, onChanged }) {
  const [open, setOpen] = useState(false);
  const [activeAction, setActiveAction] = useState(null); // {code, action}
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const flags = escalation?.red_flags || [];
  if (!flags.length && (!escalation || escalation.recommended_escalation === "none")) {
    return null;
  }

  const submit = async () => {
    if (!activeAction) return;
    if (!reason.trim()) {
      toast.error("Reason is required for this action.");
      return;
    }
    setSubmitting(true);
    try {
      const path =
        activeAction.action === "suppress"
          ? `/nutrient/${assessmentId}/override/escalation/suppress`
          : `/nutrient/${assessmentId}/override/escalation/elevate`;
      const r = await api.post(path, { flag_code: activeAction.code, reason: reason.trim() });
      if (r?.data?.ok) {
        toast.success(`Escalation ${activeAction.action}d`);
        setActiveAction(null);
        setReason("");
        onChanged?.();
      } else {
        toast.error(r?.data?.error || "Action failed");
      }
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.message("Practitioner override is temporarily disabled.");
      } else if (err?.response?.status === 403) {
        toast.error("You do not have permission for this action.");
      } else {
        toast.error(formatApiError(err, "Action failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="practitioner-escalation-controls"
      className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4"
    >
      <button
        type="button"
        data-testid="escalation-controls-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 text-left"
      >
        <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-amber-900">Escalation review</div>
          <div className="text-xs text-amber-800/80">
            {flags.length} active flag{flags.length === 1 ? "" : "s"} · suppress or elevate with reason
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-amber-700" /> : <ChevronDown className="w-4 h-4 text-amber-700" />}
      </button>

      {open ? (
        <div className="mt-4 space-y-3">
          {flags.map((flag) => {
            const code = flag.code || flag.flag_code || flag.id || "unknown";
            const label = flag.message || flag.label || flag.title || code;
            const severity = flag.severity;
            return (
              <div
                key={code}
                data-testid={`escalation-flag-${code}`}
                className="rounded-xl border border-amber-200 bg-white p-3"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 text-sm">{code}</span>
                      {severity ? (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                            severity === "hard"
                              ? "bg-rose-100 text-rose-800 border-rose-200"
                              : "bg-amber-100 text-amber-900 border-amber-200"
                          }`}
                        >
                          {severity}
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">{label}</div>
                    {flag.compliance_language ? (
                      <div className="text-xs text-slate-600 mt-1">{flag.compliance_language}</div>
                    ) : null}
                  </div>
                  <div className="flex gap-2">
                    {canSuppress ? (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`escalation-suppress-${code}`}
                        onClick={() => setActiveAction({ code, action: "suppress" })}
                      >
                        Suppress
                      </Button>
                    ) : null}
                    {canElevate ? (
                      <Button
                        size="sm"
                        variant="default"
                        data-testid={`escalation-elevate-${code}`}
                        onClick={() => setActiveAction({ code, action: "elevate" })}
                      >
                        Elevate
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}

          {activeAction ? (
            <div
              data-testid="escalation-reason-form"
              className="rounded-xl border border-purple-200 bg-white p-3 space-y-2"
            >
              <div className="text-sm font-medium text-slate-900">
                {activeAction.action === "suppress" ? "Suppress" : "Elevate"} · {activeAction.code}
              </div>
              <textarea
                data-testid="escalation-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Clinical reasoning (required, immutably logged)"
                className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:border-purple-400 focus:outline-none min-h-[80px]"
              />
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setActiveAction(null);
                    setReason("");
                  }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  data-testid="escalation-reason-submit"
                  onClick={submit}
                  disabled={submitting || !reason.trim()}
                >
                  {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Confirm"}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
